"use client";

import { isServer } from "@decibeltrade/web-core/utils";

import { env } from "@/env";
import { addressComparisonKey } from "@/utils/address";
import { Logger } from "@/utils/logger";

import { getSdkConfig } from "./dex-sdk";

// Historical key name — only read, to detect and clear pre-`network_state`
// localStorage. The string can't change without stranding that legacy data.
const LEGACY_STATE_KEY = "netna_last_transaction_timestamp";
const NETWORK_STATE_KEY = "network_state";

interface StoredData {
  configName: string;
  contractAddress: string;
}

/**
 * Function that checks the network config and contract address,
 * resetting localStorage when:
 * - The network config changes (testnet <-> mainnet switch)
 * - The contract address changes
 * - The stored data format is outdated
 */
export function checkNetworkReset() {
  if (isServer()) return;
  try {
    const config = getSdkConfig();
    const currentConfigName = env.NEXT_PUBLIC_SDK_CONFIG_NAME;
    const currentContractAddress = config.deployment.package;

    // Check for stored data in new format
    const storedDataStr = localStorage.getItem(NETWORK_STATE_KEY);

    if (storedDataStr) {
      try {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const storedData: StoredData = JSON.parse(storedDataStr);

        // Check if network config changed
        if (storedData.configName !== currentConfigName) {
          Logger.log(
            `Network config changed from ${storedData.configName} to ${currentConfigName}. Clearing localStorage and refreshing page.`,
          );
          localStorage.clear();
          window.location.reload();
          return;
        }

        // Check if contract address changed
        if (
          addressComparisonKey(storedData.contractAddress) !==
          addressComparisonKey(currentContractAddress)
        ) {
          Logger.log(
            `Contract address changed from ${storedData.contractAddress} to ${currentContractAddress}. Clearing localStorage and refreshing page.`,
          );
          localStorage.clear();
          window.location.reload();
          return;
        }

        // All checks passed, data is valid
        return;
      } catch {
        // Invalid JSON format - clear and reset
        Logger.log("Invalid data format detected. Clearing localStorage and refreshing page.");
        localStorage.clear();
        window.location.reload();
        return;
      }
    }

    // Check for legacy format (plain timestamp string) only if no new format data exists
    const legacyTimestamp = localStorage.getItem(LEGACY_STATE_KEY);
    if (legacyTimestamp && !legacyTimestamp.startsWith("{")) {
      Logger.log("Outdated data format detected. Clearing localStorage and refreshing page.");
      localStorage.clear();
      window.location.reload();
      return;
    }

    // Store the current config and contract address for next comparison
    const dataToStore: StoredData = {
      configName: currentConfigName,
      contractAddress: currentContractAddress,
    };
    localStorage.setItem(NETWORK_STATE_KEY, JSON.stringify(dataToStore));
  } catch (error) {
    Logger.error({
      message: "Failed to check network reset",
      error,
    });
  }
}
